import { createGlobalStyle } from 'styled-components';
import { colors } from '@/constants/colors';

export const GlobalStyles = createGlobalStyle`
  :root,
  [data-theme='light'] {
    --app-bg: #ffffff;
    --app-surface: #ffffff;
    --app-surface-muted: #f5f5f5;
    --app-surface-elevated: #ffffff;
    --app-border: #ebebeb;
    --app-border-strong: #cccccc;
    --app-text: #1d1d1f;
    --app-text-dim: #434346;
    --app-text-muted: #7a7a7a;
    --app-text-soft: #a3a3a3;
    --app-brand-tint: #e5eefc;
    --app-shadow: rgba(0, 0, 0, 0.08);
    --app-scrollbar: rgba(0, 0, 0, 0.2);
    color-scheme: light;
  }

  [data-theme='dark'] {
    --app-bg: #0b1030;
    --app-surface: #141838;
    --app-surface-muted: #1a1f4a;
    --app-surface-elevated: #1e2350;
    --app-border: rgba(255, 255, 255, 0.08);
    --app-border-strong: rgba(255, 255, 255, 0.16);
    --app-text: #ffffff;
    --app-text-dim: rgba(255, 255, 255, 0.78);
    --app-text-muted: rgba(255, 255, 255, 0.55);
    --app-text-soft: rgba(255, 255, 255, 0.38);
    --app-brand-tint: rgba(55, 96, 249, 0.18);
    --app-shadow: rgba(0, 0, 0, 0.55);
    --app-scrollbar: rgba(255, 255, 255, 0.18);
    color-scheme: dark;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  @media (prefers-reduced-motion: no-preference) {
    :root {
      scroll-behavior: smooth;
    }
  }

  html {
    background: var(--app-bg);
  }

  body {
    margin: 0;
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    background: var(--app-bg);
    color: var(--app-text);
    transition: background 0.25s ease, color 0.25s ease;

    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  /* Ensure hardcoded white backgrounds flip in dark mode */
  [data-theme='dark'] body { background: var(--app-bg); }
  [data-theme='dark'] input,
  [data-theme='dark'] textarea,
  [data-theme='dark'] select {
    background: var(--app-surface);
    color: var(--app-text);
    border-color: var(--app-border);
  }
  [data-theme='dark'] input::placeholder,
  [data-theme='dark'] textarea::placeholder {
    color: var(--app-text-soft);
  }

  hr {
    margin: 1rem 0;
    color: inherit;
    background-color: currentColor;
    border: 0;
    opacity: 0.25;
  }

h6,
h5,
h4,
h3,
h2,
h1 {
	margin-top: 0;
	margin-bottom: 0;
	font-weight: 500;
	line-height: 1.2;
}

h1 {
	font-size: calc(1.375rem + 1.5vw);
}
@media (min-width: 1200px) {
	h1 {
		font-size: 2.5rem;
	}
}

h2 {
	font-size: calc(1.325rem + 0.9vw);
}
@media (min-width: 1200px) {
	h2 {
		font-size: 2rem;
	}
}

h3 {
	font-size: calc(1.3rem + 0.6vw);
}
@media (min-width: 1200px) {
	h3 {
		font-size: 1.75rem;
	}
}

h4 {
	font-size: calc(1.275rem + 0.3vw);
}
@media (min-width: 1200px) {
	h4 {
		font-size: 1.5rem;
	}
}

h5 {
	font-size: 1.25rem;
}

h6 {
	font-size: 1rem;
}

p {
	margin-top: 0;
	margin-bottom: 0;
}

abbr[title],
abbr[data-bs-original-title] {
	-webkit-text-decoration: underline dotted;
	text-decoration: underline dotted;
	cursor: help;
	-webkit-text-decoration-skip-ink: none;
	text-decoration-skip-ink: none;
}

address {
	margin-bottom: 1rem;
	font-style: normal;
	line-height: inherit;
}

dt {
	font-weight: 700;
}

dd {
	margin-bottom: 0.5rem;
	margin-left: 0;
}

blockquote {
	margin: 0 0 1rem;
}

b,
strong {
	font-weight: bolder;
}

small {
	font-size: 0.875em;
}

mark {
	padding: 0.2em;
	background-color: #fcf8e3;
}

sub,
sup {
	position: relative;
	font-size: 0.75em;
	line-height: 0;
	vertical-align: baseline;
}

sub {
	bottom: -0.25em;
}

sup {
	top: -0.5em;
}

a {
	color: #0d6efd;
	text-decoration: none;
}
a:hover {
	color: #0a58ca;
}

a:not([href]):not([class]),
a:not([href]):not([class]):hover {
	color: inherit;
	text-decoration: none;
}

ul {
	padding: 0;
	margin-bottom: 0;
}

pre,
code,
kbd,
samp {
	font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
		"Courier New", monospace;
	font-size: 1em;
	direction: ltr /* rtl:ignore */;
	unicode-bidi: bidi-override;
}

pre {
	display: block;
	margin-top: 0;
	margin-bottom: 1rem;
	overflow: auto;
	font-size: 0.875em;
}
pre code {
	font-size: inherit;
	color: inherit;
	word-break: normal;
}

code {
	font-size: 0.875em;
	color: #d63384;
	word-wrap: break-word;
}
a > code {
	color: inherit;
}

kbd {
	padding: 0.2rem 0.4rem;
	font-size: 0.875em;
	color: #fff;
	background-color: #212529;
	border-radius: 0.2rem;
}
kbd kbd {
	padding: 0;
	font-size: 1em;
	font-weight: 700;
}

figure {
	margin: 0 0 1rem;
}

img {
	width: 100%;
}
svg {
	vertical-align: middle;
}

table {
	caption-side: bottom;
	border-collapse: collapse;
}

caption {
	padding-top: 0.5rem;
	padding-bottom: 0.5rem;
	color: #6c757d;
	text-align: left;
}

th {
	text-align: inherit;
	text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
	border-color: inherit;
	border-style: solid;
	border-width: 0;
}

label {
	display: inline-block;
}

button {
	border-radius: 0;
}

button:focus:not(:focus-visible) {
	outline: 0;
}

input,
button,
select,
optgroup,
textarea {
	margin: 0;
	font-family: inherit;
	font-size: inherit;
	line-height: inherit;
}

button,
select {
	text-transform: none;
}

[role="button"] {
	cursor: pointer;
}

select {
	word-wrap: normal;
}
select:disabled {
	opacity: 1;
}

[list]::-webkit-calendar-picker-indicator {
	display: none;
}

button,
[type="button"],
[type="reset"],
[type="submit"] {
	-webkit-appearance: button;
}
button:not(:disabled),
[type="button"]:not(:disabled),
[type="reset"]:not(:disabled),
[type="submit"]:not(:disabled) {
	cursor: pointer;
}

::-moz-focus-inner {
	padding: 0;
	border-style: none;
}

textarea {
	resize: vertical;
}

fieldset {
	min-width: 0;
	padding: 0;
	margin: 0;
	border: 0;
}

legend {
	float: left;
	width: 100%;
	padding: 0;
	margin-bottom: 0.5rem;
	font-size: calc(1.275rem + 0.3vw);
	line-height: inherit;
}
@media (min-width: 1200px) {
	legend {
		font-size: 1.5rem;
	}
}
legend + * {
	clear: left;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
	padding: 0;
}

::-webkit-inner-spin-button {
	height: auto;
}

[type="search"] {
	outline-offset: -2px;
	-webkit-appearance: textfield;
}

/* rtl:raw:
  [type="tel"],
  [type="url"],
  [type="email"],
  [type="number"] {
    direction: ltr;
  }
  */
::-webkit-search-decoration {
	-webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
	padding: 0;
}

::file-selector-button {
	font: inherit;
}

::-webkit-file-upload-button {
	font: inherit;
	-webkit-appearance: button;
}

output {
	display: inline-block;
}

iframe {
	border: 0;
}

summary {
	display: list-item;
	cursor: pointer;
}

progress {
	vertical-align: baseline;
}

[hidden] {
	display: none !important;
}
.react-datepicker {
	width: 100%;
	border: 1px solid red;
}
.react-datepicker__month-container {
	width: 100%;
}

.slide {
	height: 600px;
	overflow: hidden;
	padding: 5px;

	display: flex;
	justify-content: center;
}


.container {
	max-width: 1300px;
	margin-left: auto;
	margin-right: auto;
	padding-left: 1rem;
	padding-right: 1rem;
	min-width: 0;
}

@media (max-width: 768px) {
	.container {
		padding-left: 0;
		padding-right: 0;
		max-width: 100vw;
		overflow-x: hidden;
	}
}

   /* A row a notification pointed at (useFocusFromQuery). */
   @keyframes smartfiFocusFlash {
      0% { box-shadow: 0 0 0 0 rgba(55, 96, 249, 0.55); }
      40% { box-shadow: 0 0 0 6px rgba(55, 96, 249, 0.25); }
      100% { box-shadow: 0 0 0 0 rgba(55, 96, 249, 0); }
   }
   .focus-flash {
      animation: smartfiFocusFlash 1.3s ease-out 2;
      border-radius: 10px;
   }
   @media (prefers-reduced-motion: reduce) {
      .focus-flash { animation: none; outline: 2px solid #3760f9; outline-offset: 2px; }
   }
`;
